import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand, { BrandColours } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { between, Device, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { h5, h6, paragraphSmall } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
import AnnouncementBarStyles from '../AnnouncementBar/AnnouncementBar.styles';
const Container = styled.nav `
  @media ${until(Device.TabletLarge)} {
    ${transition('opacity, visibility, transform', 0.5)};

    position: fixed;
    z-index: ${Layers.Navigation};
    top: calc(var(--headerHeight));
    right: 0;
    width: 100%;
    height: 100%;
    max-height: calc(100dvh - var(--headerHeight));
    display: grid;
    grid-template-rows: auto 1fr;
    gap: 40px;
    padding: 24px var(--sitePadding) 30px;
    max-width: 767px;
    background-color: ${brand.global.white};
    box-shadow: inset -4px 4px 8px 0px rgba(0, 0, 0, 0.08), 0 4px 8px 0px rgba(0, 0, 0, 0.08);
    overscroll-behavior: contain;

    &[data-active='false'] {
      opacity: 0;
      pointer-events: none;
      visibility: hidden;
      transform: translateX(100%);
    }
  }

  @media ${from(Device.TabletLarge)} {
    flex: 2 1 100%;
  }
`;
const Background = styled.div `
  ${transition('opacity, visibility, transform', 0.5)};

  position: fixed;
  z-index: -1;
  top: var(--headerHeight);
  right: 0;
  width: 100%;
  height: 100%;
  max-height: calc(100dvh - var(--headerHeight));
  backdrop-filter: blur(3px);
  cursor: zoom-out;

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
`;
const Links = styled.ul `
  display: flex;
  flex-direction: column;

  @media ${until(Device.TabletLarge)} {
    position: relative;
    overflow: auto;
    grid-row: 1;
    grid-column: 1;
    overscroll-behavior: contain;
  }

  @media ${from(Device.TabletLarge)} {
    flex-direction: row;
    gap: ${fluid(8, 18, Device.Desktop)};

    li {
      position: relative;
    }
  }
`;
const TopLevelLink = styled.a `
  ${ButtonReset};
  ${fonts.lato.semiBold};

  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px;
  font-size: 16px;
  line-height: 24px;
  text-decoration: none;
  color: ${brand.global.black};
  text-align: left;

  svg {
    width: 24px;
    margin-left: auto;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    width: 100%;

    &[aria-haspopup] svg {
      transform: rotate(-90deg);
    }

    &:active {
      transform: translateY(1.5px);
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${transition('background-color', 0.25)};

    padding: 0 6px;
    background-color: transparent;
    height: ${fluid(42, 48)};
    line-height: 1.5;
    border-radius: 10px;
    font-size: ${fluid(12, 16, Device.Desktop)};

    &:hover,
    &:focus-visible {
      background-color: ${rgba(brand.primary.blue.light, 0.2)};
    }

    svg {
      ${transition('transform', 0.25)};

      width: 20px;
    }

    &[data-link-only] {
      svg {
        display: none;
      }
    }

    &[aria-expanded='true'] svg {
      transform: rotate(180deg);
    }
  }

  @media ${from(Device.Desktop)} {
    white-space: nowrap;
  }
`;
const BottomContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  margin-top: auto;

  ${ButtonStyles.Container} {
    --buttonHeight: 44px;
  }

  ${AnnouncementBarStyles.CtaWrapper} a {
    --textLinkColour: ${brand.global.black};
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const Dropdown = styled.div `
  display: contents;

  @media ${from(Device.TabletLarge)} {
    --spacing: ${fluid(28, 46, Device.DesktopSmall)};

    display: grid;
    max-height: calc(100vh - var(--headerHeight) - 32px);
    grid-template-columns: 0.9fr 1fr 1fr 1fr;
    overscroll-behavior: contain;

    &[data-relative='true'] {
      position: absolute;
      top: calc(100% + ${fluid(30, 32)});
      left: 50%;
      transform: translateX(-50%);

      @media ${from(Device.DesktopSmall)} {
        left: 0;
        transform: none;
      }
    }

    &[data-relative='false'] {
      ${siteWide};

      position: fixed;
      left: 50%;
      transform: translateX(-50%);
      top: calc(var(--headerHeight) + 16px);
    }
  }

  &[data-active='false'] {
    pointer-events: none;
  }
`;
const Level = styled.ul `
  ${transition('opacity, visibility, transform', 0.5)};

  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: auto;
  background-color: ${brand.global.white};
  overscroll-behavior: contain;

  &[data-full='true'] {
    display: grid;

    &:has(${() => SubLink}[data-card]) {
      gap: ${fluid(16, 24)} 16px;
    }
  }

  @media ${until(Device.TabletLarge)} {
    grid-row: 1;
    grid-column: 1;
    margin: -16px 0 0 calc(var(--sitePadding) * -1);
    height: calc(100% + 16px);
    width: calc(100% + var(--sitePadding) * 2);
    z-index: 1;

    ${Dropdown}[data-relative='true'] & {
      position: fixed;
      top: 0;
      right: 0;
      height: calc(100% - 146px);
      width: calc(100% + var(--sitePadding));
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${scrollbars};

    gap: ${fluid(4, 8)};
    border-radius: 10px;
    padding: ${fluid(12, 14, Device.DesktopSmall)};
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
    max-height: calc(100vh - var(--headerHeight) - 32px);

    ${Dropdown}[data-relative='true'] & {
      width: ${fluid(320, 404, Device.TabletLarge, Device.Desktop)};
    }

    ${Dropdown}[data-relative='false'] & {
      grid-row: 1 / span 1;
      grid-column: 1 / span 1;

      &[data-level] {
        position: relative;
        padding-left: var(--spacing);
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        box-shadow: -8px 0px 0px 0px #fff, 0px 4px 8px 0px rgba(0, 0, 0, 0.08);

        &::before {
          content: '';
          position: absolute;
          width: 1.5px;
          height: calc(100% - 32px);
          background-color: ${brand.grey.lightest};
          top: 16px;
          left: calc(var(--spacing) * 0.5 - 0.75px);
        }
      }

      &[data-level='1'] {
        grid-column: 2 / span 1;
      }

      &[data-level='2'] {
        grid-column: 3 / span 1;
      }

      &[data-level='3'] {
        grid-column: 4 / span 1;
      }

      &[data-full='true'] {
        grid-column: 2 / span 3;
        grid-template-columns: repeat(3, 1fr);
        grid-auto-rows: min-content;
      }

      &[data-level][data-active='false'] {
        transform: translateX(-20px);
      }
    }

    li[data-mobile] {
      display: none;
    }
  }

  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    transform: translateX(100%);

    @media ${from(Device.TabletLarge)} {
      &[data-active='false'] {
        transform: translateY(-20px);
      }
    }
  }
`;
const SubLink = styled.a `
  ${transition('background-color', 0.25)};
  ${ButtonReset};

  --bgColour: transparent;
  --hoverColour: ${rgba(brand.primary.blue.light, 0.2)};
  --textColour: ${brand.global.black};

  display: block;
  padding: 8px;
  text-decoration: none;
  color: var(--textColour);
  border-radius: 10px;
  background-color: var(--bgColour);
  text-align: left;
  width: 100%;

  &[data-parent='true'] {
    --bgColour: ${brand.primary.purple.light};
    --hoverColour: ${brand.primary.purple.dark};
    --textColour: ${brand.global.white};

    margin-bottom: ${fluid(2, 4)};
    padding: ${fluid(10, 12)};
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform, background-color', 0.15)};

    margin: 0 var(--sitePadding);
    width: calc(100% - var(--sitePadding) * 2);
    padding: 12px;

    &:active {
      transform: translateY(1.5px);
    }
  }

  &[data-active='true'],
  &:hover,
  &:focus-visible {
    background-color: var(--hoverColour);
  }

  &[data-card] {
    background-color: ${brand.primary.grey};
    height: 100%;
  }

  ${ButtonStyles.Container} {
    flex: 0 0 var(--buttonHeight);

    @media ${from(Device.TabletLarge)} {
      --buttonHeight: ${fluid(26, 42, Device.TabletLarge)} !important;

      ${ButtonStyles.IconWrapper} {
        --iconSize: ${fluid(20, 32)};
      }
    }
  }
`;
const SubLinkTitle = styled.span `
  ${transition('text-decoration-color')};
  ${fonts.lato.semiBold};

  display: flex;
  gap: ${fluid(8, 10)};
  align-items: center;
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(24, 26)};
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;
  text-underline-offset: 3px;

  @media ${between(Device.TabletLarge, Device.DesktopSmall)} {
    font-size: ${fluid(14, 16, Device.TabletLarge, Device.Desktop)};
    line-height: ${fluid(20, 22, Device.TabletLarge, Device.Desktop)};
  }

  > svg {
    ${transition('transform', 0.25)};

    width: 24px;
  }

  > svg,
  ${ButtonStyles.Container} {
    margin-left: auto;
  }

  ${SubLink}button& {
    svg {
      transform: rotate(-90deg);
    }
  }

  ${SubLink}[data-parent='true'] & {
    ${h5};

    margin: 0;

    @media ${from(Device.TabletLarge)} {
      ${h6};

      margin: 0;
    }
  }

  ${SubLink}:hover &,
  ${SubLink}:focus-visible &,
  ${SubLink}[data-active='true'] & {
    text-decoration-color: currentColor;
  }

  ${SubLink}:hover &,
  ${SubLink}:focus-visible & {
    > svg {
      transform: translateX(4px);
    }
  }
`;
const SubLinkDescription = styled.p `
  ${paragraphSmall};

  margin: ${fluid(4, 8)} 0 0;

  @media ${between(Device.TabletLarge, Device.Desktop)} {
    font-size: ${fluid(12, 14, Device.TabletLarge, Device.Desktop)};
    line-height: ${fluid(18, 20, Device.TabletLarge, Device.Desktop)};
  }
`;
const SubLinkIcon = styled.span `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--iconColour, ${brand.primary.purple.light});

  svg {
    display: block;
    width: 100%;
    height: 100%;
  }

  &[data-colour=${BrandColours.BlueDark}] {
    --iconColour: ${brand.primary.blue.dark};
  }

  &[data-colour=${BrandColours.BlueLight}] {
    --iconColour: ${brand.primary.blue.light};
  }

  &[data-colour=${BrandColours.GreenDark}] {
    --iconColour: ${brand.secondary.green.dark};
  }

  &[data-colour=${BrandColours.GreenLight}] {
    --iconColour: ${brand.secondary.green.dark};
  }

  &[data-colour=${BrandColours.OrangeDark}] {
    --iconColour: ${brand.secondary.orange.dark};
  }

  &[data-colour=${BrandColours.OrangeLight}] {
    --iconColour: ${brand.secondary.orange.light};
  }

  &[data-colour=${BrandColours.PinkDark}] {
    --iconColour: ${brand.secondary.pink.dark};
  }

  &[data-colour=${BrandColours.PinkLight}] {
    --iconColour: ${brand.secondary.pink.light};
  }

  &[data-colour=${BrandColours.PurpleDark}] {
    --iconColour: ${brand.primary.purple.dark};
  }

  @media ${from(Device.TabletLarge)} {
    width: 28px;
    height: 28px;
  }
`;
const SubLinkImage = styled.div `
  ${imageWrapperPreserveRatio(72, 102)};

  width: 72px;
`;
const Image = styled.img `
  ${imageCoverContainer()}
`;
const BackButton = styled.button `
  ${ButtonReset};
  ${fonts.intro};
  ${transition('transform', 0.15)};

  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 20px;
  line-height: 26px;
  padding: 16px var(--sitePadding);
  margin: 0 0 16px;
  border-bottom: 1px solid ${brand.primary.grey};
  width: 100%;
  text-align: left;

  &:active {
    transform: translateY(1.5px);
  }

  svg {
    width: 32px;
    transform: scale(-1);
  }

  @media ${from(Device.TabletLarge)} {
    display: none;
  }
`;
const NavigationStyles = {
    BackButton,
    Background,
    BottomContainer,
    Container,
    Dropdown,
    Image,
    Level,
    Links,
    SubLink,
    SubLinkDescription,
    SubLinkIcon,
    SubLinkImage,
    SubLinkTitle,
    TopLevelLink,
};
export default NavigationStyles;
